import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EditorModule } from 'primeng/editor';
import { PrivacyPolicyModel } from '../../models/privacy-policy.model';
import { PrivacyPolicyService } from '../../services/privacy-policy.service';
import { EditorSpellcheckDirective } from '../../shared/directives/editor-spellcheck.directive';
import { normalizeRichTextHtml } from '../../shared/utils/rich-text-html.util';

@Component({
  selector: 'app-privacy-policy-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, EditorModule, EditorSpellcheckDirective],
  templateUrl: './privacy-policy-admin.html',
  styleUrl: './privacy-policy-admin.css'
})
export class PrivacyPolicyAdminComponent implements OnInit {
  currentPolicy: PrivacyPolicyModel | null = null;
  title = 'Navigator Privacy Policy';
  contentHtml = '';
  loading = true;
  saving = false;
  message = '';
  error = '';

  constructor(
    private privacyPolicyService: PrivacyPolicyService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.loadCurrentPolicy();
  }

  loadCurrentPolicy(): void {
    this.loading = true;
    this.privacyPolicyService.getCurrentPolicy().subscribe({
      next: policy => {
        this.currentPolicy = policy;
        this.title = policy.title;
        this.contentHtml = policy.contentHtml;
        this.loading = false;
      },
      error: error => {
        if (error?.status === 404) {
          this.currentPolicy = null;
          this.title = 'Navigator Privacy Policy';
          this.contentHtml = '';
          this.error = '';
          this.loading = false;
          return;
        }

        this.loading = false;
        this.error = 'Unable to load current privacy policy.';
      }
    });
  }

  publish(): void {
    const title = this.title.trim();
    const contentHtml = this.contentHtml.trim();

    if (!title || !contentHtml || this.saving) {
      this.error = 'Title and content are required.';
      return;
    }

    this.saving = true;
    this.error = '';
    this.message = '';

    this.privacyPolicyService.publish({ title, contentHtml }).subscribe({
      next: policy => {
        this.currentPolicy = policy;
        this.title = policy.title;
        this.contentHtml = policy.contentHtml;
        this.saving = false;
        this.message = `Privacy policy version ${policy.versionNumber} published. Users must accept the updated wording.`;
      },
      error: error => {
        this.saving = false;
        this.error = error?.error?.message || 'Unable to publish privacy policy.';
      }
    });
  }

  get previewHtml(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(normalizeRichTextHtml(this.contentHtml));
  }
}
